/**
 * RoutersList — List view for routers.
 * Follows the wol-container pattern per instructionsUI.md.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { SignalIcon } from '@heroicons/react/24/outline';
import RouterForm from './RouterForm';
import useNavOrder from '../hooks/useNavOrder';
import { useAutoOpen } from '../hooks/useAutoOpen';
import { getServerFormStyle } from '../utils/serverFormStyle';
import api from '../services/api';
import ExportExcelButton from './ExportExcelButton';

const STATUS_BADGE = {
  active:      { bg: '#dcfce7', color: '#15803d', label: 'Activo' },
  standby:     { bg: '#e0f2fe', color: '#0369a1', label: 'Standby' },
  maintenance: { bg: '#fef9c3', color: '#854d0e', label: 'Mantenimiento' },
  decom:       { bg: '#fee2e2', color: '#991b1b', label: 'Decom' },
};

const VENDOR_ICON = {
  cisco:    '🔵',
  juniper:  '🌀',
  mikrotik: '🟢',
  huawei:   '🔴',
  arista:   '🟣',
  ubiquiti: '🔷',
  hpe:      '🟠',
  nokia:    '⚪',
  paloalto: '🟠',
  fortinet: '🔴',
  generic:  '🖥️',
  other:    '📡',
};

const ROLE_BADGE = {
  core:             { bg: '#fce7f3', color: '#9d174d' },
  distribution:     { bg: '#ede9fe', color: '#5b21b6' },
  access:           { bg: '#dcfce7', color: '#15803d' },
  edge:             { bg: '#fff7ed', color: '#9a3412' },
  border:           { bg: '#fef3c7', color: '#92400e' },
  wan:              { bg: '#e0f2fe', color: '#0369a1' },
  vpn_concentrator: { bg: '#f0fdf4', color: '#166534' },
  route_reflector:  { bg: '#dbeafe', color: '#1e40af' },
  pe:               { bg: '#fef9c3', color: '#854d0e' },
  ce:               { bg: '#fee2e2', color: '#991b1b' },
  other:            { bg: '#f1f5f9', color: '#475569' },
};

export default function RoutersList({ companyId }) {
  const [routers, setRouters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // null = list, {} = new, router = edit
  const [search, setSearch] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [filterRole, setFilterRole] = useState('');
  // Los filtros nacen plegados: el buscador se queda siempre a la vista.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [hlId, setHlId] = useState(null); // resaltado ?destacar

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await api.getRouters(companyId);
      setRouters(res.data || []);
    } catch (e) {
      console.error('RoutersList load error', e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // Resalta el elemento indicado por ?destacar=<id> (desde el Diseñador)
  // y con `&abrir=1` abre además su ficha (buscador global, ficha → ficha).
  useEffect(() => {
    if (loading || !routers.length) return;
    const params = new URLSearchParams(window.location.search);
    const d = Number(params.get('destacar'));
    if (!d) return;
    if (params.get('abrir') === '1') {
      const target = routers.find((r) => Number(r.id) === d);
      if (target) { setSelected(target); return; }
    }
    setHlId(d);
    const t1 = setTimeout(() => {
      const el = document.getElementById(`router-row-${d}`);
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 120);
    const t2 = setTimeout(() => setHlId(null), 6000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routers, loading]);

  const handleSave  = () => { setSelected(null); load(); };
  const handleClose = () => setSelected(null);
  useAutoOpen(routers, setSelected);
  // Si el formulario acaba de crear el router, el listado lo adopta como
  // seleccionado: la ficha se queda abierta en modo edición en vez de cerrarse.
  const handleFormSave = (creado) => {
    load();
    if (creado?.id) setSelected(creado);
  };

  const filtered = routers.filter(r => {
    if (filterVendor && r.vendor !== filterVendor) return false;
    if (filterRole && r.router_role !== filterRole) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      r.name?.toLowerCase().includes(s) ||
      r.hostname?.toLowerCase().includes(s) ||
      r.model?.toLowerCase().includes(s) ||
      r.vendor_label?.toLowerCase().includes(s) ||
      r.router_role_label?.toLowerCase().includes(s) ||
      r.location_name?.toLowerCase().includes(s) ||
      r.rack_name?.toLowerCase().includes(s)
    );
  });

  // Collect unique vendors/roles for filters
  const uniqueVendors = [...new Set(routers.map(r => r.vendor))].sort();
  const uniqueRoles = [...new Set(routers.map(r => r.router_role))].sort();

  const exportRows = useMemo(() => filtered.map(r => {
    const badge = STATUS_BADGE[r.status] || STATUS_BADGE.active;
    const protocols = (r.routing_protocols || '').split(',').filter(Boolean);
    return {
      'Nombre': r.name,
      'Hostname': r.hostname || '',
      'Fabricante': r.vendor_label || r.vendor || '',
      'Modelo': r.model || '',
      'Rol': r.router_role_label || r.router_role || '',
      'Protocolos de routing': protocols.map(p => p.toUpperCase()).join(', '),
      'Ubicación': r.location_name || '',
      'Rack': r.rack_name || '',
      'Posición (U)': r.position_u ?? '',
      'Formato': r.form_factor || '',
      'Nº interfaces': r.interface_count ?? 0,
      'HA': r.ha_enabled
        ? (r.ha_role === 'primary' ? 'Primario' : r.ha_role === 'secondary' ? 'Secundario' : 'Sí')
        : 'No',
      'Estado': badge.label
    };
  }), [filtered]);

  // ── Detail overlay mode ───────────────────────────────────────────────────
  const navRouters = useNavOrder(filtered, selected, selected !== null);
  if (selected !== null) {
    return (
      <RouterForm
        router={selected.id ? selected : null}
        companyId={companyId}
        variant={getServerFormStyle() === 'cards' ? 'cards' : 'classic'}
        navItems={navRouters}
        navIndex={selected.id ? navRouters.findIndex(r => String(r.id) === String(selected.id)) : -1}
        onNavigate={(destino) => setSelected(destino)}
        onClose={handleClose}
        onSave={handleFormSave}
      />
    );
  }

  // ── List mode ─────────────────────────────────────────────────────────────
  return (
    <div className="wol-container">
      {/* Header */}
      <div className="wol-header">
        <div className="wol-header-left">
          <SignalIcon className="wol-icon" />
          <div>
            <h1 className="wol-title">Routers</h1>
            <p className="wol-subtitle">{routers.length} registros · {filtered.length} filtrados</p>
          </div>
        </div>
        <button
          type="button"
          className="wol-btn-secondary"
          onClick={() => setFiltersOpen((open) => !open)}
          aria-expanded={filtersOpen}
        >
          Filtros
          {[filterVendor, filterRole].filter(Boolean).length > 0
            ? ` (${[filterVendor, filterRole].filter(Boolean).length})`
            : ''}
        </button>
        <ExportExcelButton rows={exportRows} fileBase="routers" sheetName="Routers" />
        <button className="wol-btn-primary" onClick={() => setSelected({})}>＋ Nuevo router</button>
      </div>

      {/* Filtros: plegados bajo el botón «Filtros» */}
      <div className="wol-filters">
        <input
          className="wol-search"
          placeholder="Buscar por nombre, hostname, modelo…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {filtersOpen && (
          <>
            <select className="wol-select" value={filterVendor} onChange={e => setFilterVendor(e.target.value)}>
              <option value="">Todos los fabricantes</option>
              {uniqueVendors.map(v => (
                <option key={v} value={v}>{VENDOR_ICON[v] || '📡'} {v}</option>
              ))}
            </select>
            <select className="wol-select" value={filterRole} onChange={e => setFilterRole(e.target.value)}>
              <option value="">Todos los roles</option>
              {uniqueRoles.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* Loading */}
      {loading && <div className="wol-loading"><div className="spinner-border text-primary" /><span>Cargando</span></div>}

      {/* Empty */}
      {!loading && filtered.length === 0 && (
        <div className="wol-empty">
          <span>📡</span>
          <p className="empty-title">No hay routers todavía</p>
          <p className="empty-subtitle">Registra los routers de la infraestructura para inventariarlos.</p>
          <button className="wol-btn-primary" onClick={() => setSelected({})}>＋ Crear primer router</button>
        </div>
      )}

      {/* Table */}
      {!loading && filtered.length > 0 && (
        <div className="wol-table-wrap">
          <table className="wol-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Vendor / Modelo</th>
                <th>Rol</th>
                <th>Protocolos</th>
                <th>Ubicación / Rack</th>
                <th>Interfaces</th>
                <th>HA</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const badge = STATUS_BADGE[r.status] || STATUS_BADGE.active;
                const icon  = VENDOR_ICON[r.vendor] || '📡';
                const rb    = ROLE_BADGE[r.router_role] || ROLE_BADGE.other;
                const protocols = (r.routing_protocols || '').split(',').filter(Boolean);
                return (
                  <tr key={r.id} id={`router-row-${r.id}`} onClick={() => setSelected(r)} style={{ cursor: 'pointer', ...(r.id === hlId ? { outline: '2px solid #f59e0b', outlineOffset: -2, background: 'rgba(245,158,11,.10)' } : {}) }}>
                    <td>
                      <span style={{ marginRight: 6 }}>{icon}</span>
                      <strong>{r.name}</strong>
                      {r.hostname && (
                        <span style={{ marginLeft: 6, fontSize: '0.78rem', color: '#64748b' }}>
                          ({r.hostname})
                        </span>
                      )}
                    </td>
                    <td>
                      <span style={{ fontWeight: 500 }}>{r.vendor_label || '—'}</span>
                      {r.model && (
                        <span style={{ marginLeft: 4, fontSize: '0.8rem', color: '#64748b' }}>
                          · {r.model}
                        </span>
                      )}
                    </td>
                    <td>
                      <span style={{
                        background: rb.bg, color: rb.color,
                        borderRadius: 4, padding: '1px 7px',
                        fontSize: '0.72rem', fontWeight: 600,
                      }}>
                        {r.router_role_label || r.router_role}
                      </span>
                    </td>
                    <td>
                      {protocols.length > 0 ? (
                        <span style={{ fontSize: '0.78rem', color: '#475569' }}>
                          {protocols.slice(0, 3).map(p => p.toUpperCase()).join(', ')}
                          {protocols.length > 3 && ` +${protocols.length - 3}`}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td>
                      {r.location_name ? (
                        <div style={{ lineHeight: 1.4 }}>
                          <span style={{ fontSize: '0.82rem', color: '#334155' }}>{r.location_name}</span>
                          {r.rack_name && (
                            <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b' }}>
                              🗄 {r.rack_name}{r.position_u ? ` · U${r.position_u}` : ''}
                            </span>
                          )}
                          {!r.rack_name && r.form_factor && r.form_factor !== 'rack' && (
                            <span style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8' }}>
                              {r.form_factor}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{
                        background: 'var(--bg-toolbar)', borderRadius: 10,
                        padding: '1px 9px', fontSize: '0.82rem', fontWeight: 600,
                        color: r.interface_count > 0 ? '#93c5fd' : 'var(--text-muted)',
                      }}>
                        {r.interface_count}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {r.ha_enabled
                        ? <span style={{ background: 'rgba(14,165,233,.16)', color: '#7dd3fc', borderRadius: 4, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 600 }}>
                            {r.ha_role === 'primary' ? 'Primario' : r.ha_role === 'secondary' ? 'Secundario' : 'HA'}
                          </span>
                        : <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>—</span>
                      }
                    </td>
                    <td>
                      <span style={{
                        background: badge.bg, color: badge.color,
                        borderRadius: 4, padding: '2px 8px',
                        fontSize: '0.72rem', fontWeight: 600,
                      }}>
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
